import {
  FileText,
  Upload,
  ExternalLink,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ItemAttachment } from '../../../types';

interface AttachmentsTabProps {
  readonly attachments?: ItemAttachment[];
  readonly onUploadFile?: () => void;
}

export function AttachmentsTab({
  attachments, onUploadFile 
}: AttachmentsTabProps) {
  const hasAttachments = attachments && attachments.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-gray-900">
          Documents & Files {hasAttachments ? `(${attachments.length})` : ''}
        </h3>
        <Button variant="outline" size="sm" onClick={onUploadFile}>
          <Upload className="mr-2 h-4 w-4" />
          Upload File
        </Button>
      </div>

      {hasAttachments ? (
        <div className="space-y-2">
          {attachments.map((attachment) => (
            <AttachmentCard key={attachment.attachmentId} attachment={attachment} />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={FileText}
          title="No attachments"
          description="Upload product specifications, supplier documents, or other relevant files."
        />
      )}
    </div>
  );
}

interface AttachmentCardProps {readonly attachment: ItemAttachment;}

function AttachmentCard({ attachment }: AttachmentCardProps) {
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="flex items-center justify-between rounded-lg border p-4 hover:bg-gray-50">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-gray-100 p-2">
          <FileText className="h-5 w-5 text-gray-600" />
        </div>
        <div>
          <p className="font-medium text-gray-900">{attachment.fileName}</p>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>{formatFileSize(attachment.fileSize)}</span>
            <span>•</span>
            <span>Uploaded {new Date(attachment.uploadedAt).toLocaleDateString()}</span>
            {attachment.description && (
              <>
                <span>•</span>
                <span>{attachment.description}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <Button variant="ghost" size="sm">
        <ExternalLink className="h-4 w-4" />
      </Button>
    </div>
  );
}

interface EmptyStateProps {
  readonly icon: typeof Package;
  readonly title: string;
  readonly description: string;
}

function EmptyState({
  icon: emptyIcon,
  title,
  description,
}: EmptyStateProps) {
  const IconComponent = emptyIcon;
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="rounded-full bg-gray-100 p-4 mb-4">
        <IconComponent className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="font-medium text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500 max-w-sm">{description}</p>
    </div>
  );
}
